import React, { useState } from "react";
import { useParams } from "react-router-dom";
import QRCardApplication from "../components/QrcodeCard/QRCardApplication";
import { getFileHash } from "../utils/utils";
import swal from "sweetalert";
import axios from "axios";
import { Modalprogress } from "../components/Modal/Modalprogress";
import { Modalsuccessfile } from "../components/Modal/Modalsuccessfile";
import AutoFillProgress from "../components/ProgressAnimation/AutoFillProgress";
import Header from "../components/Header";

const { REACT_APP_CORD_MIDDLEWARE, REACT_APP_VERIFY_TOKEN, REACT_APP_API_URL, REACT_APP_CLIENT_TOKEN } = process.env;

const JobsPage = () => {
  const { jobId } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
    institute_name: "",
    completion_date: "",
    score: "",
    level: "",
    organization_name: "",
    role: "",
    start_date: "",
    end_date: "",
    current_location: "",
    working_days_timings: "",
    physical_work: "",
  });

  const [successModal, setSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blockHash, setBlockHash] = useState("");
  const [currentHash, setCurrentHash] = useState("");
  const [verified, setVerified] = useState();
  const [disabledFields, setDisabledFields] = useState({});
  
  // Auto-fill progress animation states
  const [showAutoFillProgress, setShowAutoFillProgress] = useState(false);
  const [autoFillPercentage, setAutoFillPercentage] = useState(0);
  const [filledFieldsCount, setFilledFieldsCount] = useState(0);
  const [totalFieldsCount, setTotalFieldsCount] = useState(0);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // success, error, info

  // Form submission loading state
  const [submissionLoading, setSubmissionLoading] = useState(false);

  // Handle file input change
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const currentHash = await getFileHash(file);
    setCurrentHash(currentHash);
    setLoading(true);
    setTimeout(() => {
      verifyFile(currentHash);
    }, [6000]);

    setFormData((prev) => ({ ...prev, file }));
  };

  const verifyFile = async (hash) => {
    const response = await axios.post(
      `${REACT_APP_CORD_MIDDLEWARE}/verify/document/verify`,
      { filehash: hash },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${REACT_APP_VERIFY_TOKEN}`,
        },
        validateStatus: (status) => status < 500,
      }
    );

    if (response.status === 200) {
      setLoading(false);
      if (response.data?.data?.revoked) {
        swal({
          title: "Revoked",
          text: "Document is Revoked",
          icon: "error",
        });
      } else {
        setBlockHash(response.data?.data?.registryEntryId);
        setVerified(true);
        setSuccessModal(true);
      }
    } else if (response.status === 400) {
      setLoading(false);
      setVerified("error");
      swal({
        title: "Not anchored on chain",
        text: response.data.err || "An error occurred",
        icon: "error",
      });
    } else {
      setLoading(false);
      setVerified(false);
      swal({
        title: "Unable to process the request",
        text: response.data.error || "An unknown error occurred",
        icon: "error",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Start loading
    setSubmissionLoading(true);

    const outputData = {
      ...formData,
      file: formData.file ? formData.file.name : null,
      jobId: jobId, // Include the job ID from the URL
    };
    
    try {
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
      
      // Show success toast
      setToastMessage(`🎉 Application submitted successfully for Job ID: ${jobId}!`);
      setToastType("success");
      setShowToast(true);

      // Wait for toast to be visible for 1.5 seconds, then show redirecting message
      setTimeout(() => {
        setToastMessage("🔄 Redirecting to fresh page...");
        setToastType("info");
      }, 1500);

      // Wait for total of 2.5 seconds, then reload page
      setTimeout(() => {
        window.location.reload(); // Refresh the entire page
      }, 2500);

    } catch (error) {
      // Show error toast if something goes wrong
      setToastMessage("❌ Failed to submit application. Please try again.");
      setToastType("error");
      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
      
      // Stop loading on error (but don't reload page)
      setSubmissionLoading(false);
    }
    // Note: No finally block needed since page reloads on success
  };

  // Function to manually close toast
  const closeToast = () => {
    setShowToast(false);
  };

  const jsonData = (data) => {
    const jsondata = JSON.parse(data);
    const credential = jsondata.VerifiableCredential[0].credentialSubject;
    const { "@context": context, ...rest } = credential;
    const keysToExtract = Object.keys(formData);

    const filteredData = Object.fromEntries(
      keysToExtract.map((key) => [key, rest[key] || ""])
    );

    // Format date fields to be more readable
    const dateFields = ["completion_date", "start_date", "end_date"];
    dateFields.forEach((field) => {
      if (filteredData[field]) {
        const formattedDate = new Date(filteredData[field]);
        if (!isNaN(formattedDate.getTime())) {
          filteredData[field] = formattedDate.toDateString();
        }
      }
    });
    

    // Calculate auto-fill progress
    const totalFields = keysToExtract.length;
    const filledFields = keysToExtract.filter((key) => rest[key] && rest[key].toString().trim() !== "").length;
    const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

    // Set progress data
    setTotalFieldsCount(totalFields);
    setFilledFieldsCount(filledFields);
    setAutoFillPercentage(percentage);

    // Show animation if fields were filled
    if (filledFields > 0) {
      setShowAutoFillProgress(true);
    }

    setFormData(filteredData);
    const disabledMap = Object.fromEntries(
      keysToExtract.map((key) => [key, !!rest[key]])
    );

    setDisabledFields(disabledMap);
  };

  // Handle auto-fill animation completion
  const handleAutoFillComplete = () => {
    setTimeout(() => {
      setShowAutoFillProgress(false);
    }, 1500); // Hide after a short delay
  };

  return (
    <div className="container-fluid">
      {/* Modern Toast Notification */}
      {showToast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            backgroundColor: toastType === "success" ? "#10B981" : toastType === "error" ? "#EF4444" : "#3B82F6",
            color: "white",
            padding: "16px 24px",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: "320px",
            maxWidth: "500px",
            animation: "slideInFromRight 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), fadeOut 0.3s ease-in-out 3.7s forwards",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)"
          }}
        >
          {/* Icon */}
          <div
            style={{
              fontSize: "20px",
              animation: "bounceIn 0.6s ease-out 0.2s both"
            }}
          >
            {toastType === "success" ? "✅" : toastType === "error" ? "❌" : "ℹ️"}
          </div>
          
          {/* Message */}
          <div
            style={{
              flex: 1,
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "1.4"
            }}
          >
            {toastMessage}
          </div>
          
          {/* Close Button */}
          <button
            onClick={closeToast}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "8px",
              color: "white",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "bold",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
          >
            ×
          </button>
        </div>
      )}

      {/* Toast Animations CSS */}
      <style jsx>{`
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          100% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes bounceIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          0% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
        }

        @media (max-width: 768px) {
          .toast-container {
            right: 10px !important;
            left: 10px !important;
            minWidth: auto !important;
            maxWidth: none !important;
          }
        }
      `}</style>

      {/* Header section with custom title for JobsPage */}
      <Header actTabHeader="apply" customTitle="Job Details" />
      
      {/* Job Description Section */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-center">
          <div style={{ maxWidth: "800px", width: "100%" }}>
            {/* <h3 className="text-center mb-3">Job Description</h3> */}
            <p style={{ 
              textAlign: "justify", 
              lineHeight: "1.6", 
              fontSize: "16px",
              padding: "0 20px",
              color: "#555"
            }}>
              We are seeking a dedicated and physically capable individual to join our warehouse team. This role involves handling, organizing, and moving inventory throughout our facility. The ideal candidate should be comfortable with physical work, including lifting and moving boxes of various sizes and weights. You will be responsible for ensuring efficient warehouse operations, maintaining inventory accuracy, and supporting the logistics team in meeting daily targets. This is a contractual position offering hands-on experience in warehouse management and logistics operations.
            </p>
          </div>
        </div>
      </div>
      
      {/* Job Details Section - Table Format */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-center">
          <div style={{ maxWidth: "800px", width: "100%" }}>
            <table className="table table-borderless mx-auto" style={{ backgroundColor: "transparent", width: "auto" }}>
              <tbody>
                <tr>
                  <td style={{ width: "200px", fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Role</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>Work</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Qualification</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>10th Pass, 12th Pass, Diploma, Graduate, ITI</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Who can Apply</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>Minimum 2 years</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Employment Type</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>Contractual</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Job Id</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>{jobId}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>State</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>Telangana</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", paddingLeft: "0", backgroundColor: "transparent", textAlign: "left" }}>Country</td>
                  <td style={{ backgroundColor: "transparent", textAlign: "left", paddingLeft: "20px" }}>India</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Line breaker after job details - limited width and darker */}
      <div className="d-flex justify-content-center">
        <hr style={{ 
          border: "none", 
          height: "2px", 
          backgroundColor: "#808080", 
          margin: "30px 0",
          width: "600px",
          maxWidth: "90%"
        }} />
      </div>

      {/* Main content */}
      <div className="row">
        <div className="col-12">
          <Modalprogress
            loading={loading}
            msg={[
              "Looking for digest on the chain...",
              `Verifying document on chain...<br/><span class="hash-text">File Hash: ${currentHash}</span>`,
            ]}
          />
          <Modalsuccessfile
            loading={successModal}
            msg={"Verification Successful"}
            txtmsg={
              "Document successfully verified on Weavenet chain."
            }
            Identifier={null}
            blockhash={blockHash}
            fileHash={currentHash}
            onClose={() => {
              setSuccessModal(false);
            }}
          />

          {/* Application Form heading and QR code section */}
          <div className="text-center mt-3">
            <h2 className="mb-4">Application Form</h2>
          </div>
          
          <QRCardApplication jsonData={jsonData} />
          
          <div className="d-flex justify-content-center align-items-center mt-5 mb-4">
            <div className="col-md-6 col-lg-5">
              {/* Auto-fill progress animation */}
              <AutoFillProgress
                isVisible={showAutoFillProgress}
                percentage={autoFillPercentage}
                totalFields={totalFieldsCount}
                filledFields={filledFieldsCount}
                onAnimationComplete={handleAutoFillComplete}
              />
              
              <form onSubmit={handleSubmit}>
                {/* Name */}
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Name
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={disabledFields.name}
                    />
                    {disabledFields.name && (
                      <span className="input-group-text">✅</span>
                    )}
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="mb-3">
                  <label htmlFor="mobile_number" className="form-label">
                    Mobile Number
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="mobile_number"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      disabled={disabledFields.mobile_number}
                    />
                    {disabledFields.mobile_number && (
                      <span className="input-group-text">✅</span>
                    )}
                  </div>
                </div>

                {/* Education Section - Side by Side Fields */}
                <div className="mb-4">
                  <h5 className="mb-3">Education Details</h5>
                  
                  {/* Institute Name and Completion Date - Side by Side */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="institute_name" className="form-label">
                        Institute Name
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="institute_name"
                          name="institute_name"
                          value={formData.institute_name}
                          onChange={handleChange}
                          disabled={disabledFields.institute_name}
                        />
                        {disabledFields.institute_name && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="completion_date" className="form-label">
                        Completion Date
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="completion_date"
                          name="completion_date"
                          value={formData.completion_date}
                          onChange={handleChange}
                          disabled={disabledFields.completion_date}
                        />
                        {disabledFields.completion_date && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Score and Level - Side by Side */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="score" className="form-label">
                        Score
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="score"
                          name="score"
                          placeholder="e.g., 85% or A Grade"
                          value={formData.score}
                          onChange={handleChange}
                          disabled={disabledFields.score}
                        />
                        {disabledFields.score && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="level" className="form-label">
                        Level
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="level"
                          name="level"
                          placeholder="e.g., 10th, 12th, Graduate"
                          value={formData.level}
                          onChange={handleChange}
                          disabled={disabledFields.level}
                        />
                        {disabledFields.level && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Experience Section - Side by Side Fields */}
                <div className="mb-4">
                  <h5 className="mb-3">Work Experience</h5>
                  
                  {/* Organization Name and Role - Side by Side */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="organization_name" className="form-label">
                        Organization Name
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="organization_name"
                          name="organization_name"
                          value={formData.organization_name}
                          onChange={handleChange}
                          disabled={disabledFields.organization_name}
                        />
                        {disabledFields.organization_name && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="role" className="form-label">
                        Role
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          disabled={disabledFields.role}
                        />
                        {disabledFields.role && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Start Date and End Date - Side by Side */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="start_date" className="form-label">
                        Start Date
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="start_date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleChange}
                          disabled={disabledFields.start_date}
                        />
                        {disabledFields.start_date && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="end_date" className="form-label">
                        End Date
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="end_date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleChange}
                          disabled={disabledFields.end_date}
                        />
                        {disabledFields.end_date && (
                          <span className="input-group-text">✅</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Location */}
                <div className="mb-3">
                  <label htmlFor="current_location" className="form-label">
                    Current Location
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="current_location"
                      name="current_location"
                      value={formData.current_location}
                      onChange={handleChange}
                      disabled={disabledFields.current_location}
                    />
                    {disabledFields.current_location && (
                      <span className="input-group-text">✅</span>
                    )}
                  </div>
                </div>

                {/* Working Days Timings */}
                <div className="mb-3">
                  <label htmlFor="working_days_timings" className="form-label">
                    Are you okay with the working days and timings?
                  </label>
                  <select
                    className="form-control"
                    id="working_days_timings"
                    name="working_days_timings"
                    value={formData.working_days_timings}
                    onChange={handleChange}
                    disabled={disabledFields.working_days_timings}
                  >
                    <option value="">Please select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* Physical Work */}
                <div className="mb-3">
                  <label htmlFor="physical_work" className="form-label">
                    Are you okay with lifting boxes physically and moving around with them?
                  </label>
                  <select
                    className="form-control"
                    id="physical_work"
                    name="physical_work"
                    value={formData.physical_work}
                    onChange={handleChange}
                    disabled={disabledFields.physical_work}
                  >
                    <option value="">Please select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {/* File Upload */}
                <div className="mb-3">
                  <label htmlFor="file" className="form-label">
                    Upload Any Additional Document (Optional)
                  </label>
                  <div className="position-relative">
                    <input
                      className={`form-control pe-5 ${
                        verified === true
                          ? "border-success"
                          : verified === "error"
                          ? "border-danger"
                          : ""
                      }`}
                      type="file"
                      id="file"
                      name="file"
                      onChange={handleFileChange}
                    />
                    {verified && (
                      <span
                        className={`position-absolute top-50 end-0 translate-middle-y me-3 fs-5 ${
                          verified === true ? "text-success" : "text-danger"
                        }`}
                        style={{ pointerEvents: "none" }}
                      >
                        {verified === true ? "✅" : "❌"}
                      </span>
                    )}
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary w-100 mb-5" 
                  disabled={submissionLoading}
                  style={{
                    opacity: submissionLoading ? 0.7 : 1,
                    cursor: submissionLoading ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  {submissionLoading ? (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <div 
                        className="spinner-border spinner-border-sm" 
                        role="status"
                        style={{ width: "1rem", height: "1rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Submitting Application...</span>
                    </div>
                  ) : (
                    "Submit Application"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsPage; 
